import CloseIcon from '@mui/icons-material/CloseOutlined'
import {
  Avatar,
  Box,
  Drawer as MuiDrawer,
  drawerClasses,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
} from '@mui/material'
import { lighten } from '@mui/system/colorManipulator'

import { CompanyMenuItems } from 'common/interfaces'
import ContactSupport from 'components/Sidebar/ContactSupport'
import StyledWrap from 'components/StyledWrap'
import { useCompany } from 'utils/company'

import Logo from '../Logo'

const styles: Record<string, SxProps<Theme>> = {
  drawer: {
    [`& .${drawerClasses.paper}`]: {
      p: (theme) => theme.spacing(1, 4, 4),
      display: 'flex',
      flexDirection: 'column',
      width: {
        xs: '100%',
        sm: '350px',
      },
    },
  },
  head: {
    mb: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    marginRight: '10px',
  },
  companyName: {
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  avatar: {
    width: '32px',
    height: '32px',
    color: (theme) => theme.palette.primary.main,
    background: (theme) => lighten(theme.palette.primary.main, 0.95),

    '& > span': {
      fontSize: '16px',
    },
  },
  itemButton: {
    py: '12px',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: '100%',
  },
}

interface DrawerProps {
  open: boolean
  onClose: () => void
}

export default function Drawer({ open, onClose }: DrawerProps): JSX.Element {
  const { data: company } = useCompany()

  const menuItems = company?.menu_items

  return (
    <StyledWrap elementName="Drawer">
      <MuiDrawer
        disablePortal
        keepMounted
        className="menu-sidebar"
        anchor="right"
        open={open}
        onClose={onClose}
        sx={styles.drawer}
      >
        <Box className="menu-sidebar-wrap" sx={styles.mainContainer}>
          <Box className="menu-sidebar-header" sx={styles.head}>
            <Logo logo={company?.logo ?? null} styles={styles.logo} />
            <IconButton className="menu-sidebar-close" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List className="menu-sidebar-list" sx={{ flex: '1 1 auto' }}>
            {Boolean(menuItems?.length) &&
              menuItems?.map(({ title, url, logo_name: logoName }: CompanyMenuItems) => (
                <ListItem key={title} disablePadding>
                  <ListItemButton href={url} target="_blank" sx={styles.itemButton} disableGutters>
                    <ListItemAvatar>
                      <Avatar sx={styles.avatar}>
                        <Icon>{logoName}</Icon>
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={title}
                      sx={styles.item}
                      primaryTypographyProps={{ sx: { fontSize: '0.875rem' } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
          <ContactSupport />
        </Box>
      </MuiDrawer>
    </StyledWrap>
  )
}
